import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Mail, Phone, MessageSquare, FileText, Sparkles, Zap, Target, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Logo } from './Logo';
import { enquiriesAPI } from '../services/api';
import { COUNTRIES } from './shared/countries';
import { EmailVerification } from './shared/EmailVerification';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    company: '',
    country: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false); // loading state
  const [isEmailVerified, setIsEmailVerified] = useState(false); // email verification state

  const modalRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Handle outside click (disabled while submitting)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSubmitting) return;
      if (backdropRef.current && event.target === backdropRef.current) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isSubmitting]);

  // Handle escape key (disabled while submitting)
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, isSubmitting]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Honeypot
    try {
      const hp = (new FormData(e.currentTarget).get('website') as string) || '';
      if (hp) return;
    } catch { }

    // Check email verification
    if (!isEmailVerified) {
      toast.error('Please verify your email address before submitting');
      return;
    }

    // Required fields validation
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.mobile.trim() ||
      !formData.country.trim() ||
      !formData.subject.trim() ||
      !formData.message.trim()
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    const mobileRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanMobile = formData.mobile.replace(/[\s\-\(\)]/g, '');
    if (!mobileRegex.test(cleanMobile) || cleanMobile.length < 7) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    setIsSubmitting(true);
    try {
      // Submit to MongoDB backend
      await enquiriesAPI.create({
        name: formData.name,
        email: formData.email,
        phone: formData.mobile,
        company: formData.company || 'N/A',
        country: formData.country,
        subject: formData.subject,
        message: formData.message,
      });

      toast.success('Thank you! Your message has been sent successfully.');
      setFormData({ name: '', email: '', mobile: '', company: '', country: '', subject: '', message: '' });
      setIsEmailVerified(false); // Reset email verification
      
      // Close modal after successful submission
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Error submitting enquiry:', err);
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={backdropRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`fixed inset-0 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto ${isSubmitting ? 'cursor-wait' : ''}`}
          style={{
            zIndex: 99999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
        >
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="relative w-full max-w-5xl mx-auto glass-strong rounded-2xl shadow-2xl overflow-hidden my-8 sm:my-12 max-h-[85vh]"
            style={{ zIndex: 99999 }}
            onClick={(e) => e.stopPropagation()}
            aria-busy={isSubmitting}
          >
            {/* Close Button - Absolute positioned */}
            <Button
              variant="ghost"
              size="icon"
              onClick={!isSubmitting ? onClose : undefined}
              className="absolute top-4 right-4 z-50 hover:bg-destructive/10 hover:text-destructive rounded-full h-8 w-8 disabled:opacity-50"
              disabled={isSubmitting}
              aria-disabled={isSubmitting}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </Button>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left Side - Image and Animation Elements - Hidden on mobile */}
              <div className="hidden lg:flex relative bg-gradient-to-br from-primary/20 via-background to-secondary/20 p-8 xl:p-10 flex-col justify-center items-center overflow-hidden">
                {/* Background Pattern */}
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#00cfff" fill-opacity="0.4"><path d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/></g></g></svg>')}")`
                  }}
                />

                {/* Animated Gradient Orbs */}
                <motion.div
                  className="absolute top-20 left-10 w-32 h-32 bg-primary/30 rounded-full blur-3xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <motion.div
                  className="absolute bottom-20 right-10 w-40 h-40 bg-secondary/30 rounded-full blur-3xl"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                />
                <motion.div
                  className="absolute top-1/2 left-1/4 w-24 h-24 bg-accent/30 rounded-full blur-2xl"
                  animate={{
                    scale: [1, 1.4, 1],
                    opacity: [0.2, 0.4, 0.2],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2
                  }}
                />

                {/* Content */}
                <div className="relative z-10 text-center space-y-4">
                  {/* Logo Container */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="relative w-full max-w-xs mx-auto mb-4 flex items-center justify-center"
                  >
                    <div className="glass p-8 rounded-2xl border-2 border-primary/30 shadow-2xl backdrop-blur-xl flex items-center justify-center">
                      <div className="scale-[2.5] flex items-center justify-center">
                        <Logo showText={false} size="lg" />
                      </div>
                    </div>

                    {/* Floating Icons */}
                    <motion.div
                      className="absolute -top-4 -right-4 bg-primary/20 backdrop-blur-sm border border-primary/30 rounded-full p-3"
                      animate={{
                        y: [0, -10, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <Sparkles className="w-6 h-6 text-primary" />
                    </motion.div>

                    <motion.div
                      className="absolute -bottom-4 -left-4 bg-secondary/20 backdrop-blur-sm border border-secondary/30 rounded-full p-3"
                      animate={{
                        y: [0, 10, 0],
                      }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0.5
                      }}
                    >
                      <Zap className="w-6 h-6 text-secondary" />
                    </motion.div>

                    <motion.div
                      className="absolute top-1/2 -right-6 bg-accent/20 backdrop-blur-sm border border-accent/30 rounded-full p-3"
                      animate={{
                        x: [0, 10, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                      }}
                    >
                      <Target className="w-6 h-6 text-accent" />
                    </motion.div>
                  </motion.div>

                  {/* Text Content */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <h2 className="text-2xl lg:text-3xl font-bold mb-3 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                      Let's Build Something Amazing
                    </h2>
                    <p className="text-muted-foreground text-sm text-justify max-w-md mx-auto">
                      Transform your business with cutting-edge AI solutions. Our team of experts is ready to help you achieve your digital transformation goals.
                    </p>
                  </motion.div>

                  {/* Feature Pills */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="flex flex-wrap gap-2 justify-center"
                  >
                    <div className="glass px-3 py-1.5 rounded-full text-xs border border-primary/30">
                      <span className="text-primary">●</span> AI-Powered
                    </div>
                    <div className="glass px-3 py-1.5 rounded-full text-xs border border-secondary/30">
                      <span className="text-secondary">●</span> 24/7 Support
                    </div>
                    <div className="glass px-3 py-1.5 rounded-full text-xs border border-accent/30">
                      <span className="text-accent">●</span> Fast Response
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Right Side - Contact Form */}
              <div className="flex flex-col">
                {/* Header */}
                <div className="px-6 lg:px-8 py-4 lg:py-6 border-b border-border/30 flex-shrink-0">
                  <h3 className="text-xl lg:text-2xl font-semibold text-foreground">Get Started</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fill in the details and we'll get back to you within 24 hours
                  </p>
                </div>

                {/* Form - Scrollable */}
                <div className="flex-1 px-6 lg:px-8 py-4 lg:py-6 overflow-y-auto">
                  <form onSubmit={handleSubmit} className="space-y-4" aria-busy={isSubmitting}>
                    {/* honeypot */}
                    <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

                    {/* Name and Email - 2 column on sm+ */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Name Field */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-foreground text-sm">
                          <User className="w-4 h-4 inline mr-2" />
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="glass border-border/30 focus:border-primary/50"
                          placeholder="Enter your full name"
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Email Field with Verification */}
                      <EmailVerification
                        email={formData.email}
                        onEmailChange={(email) => setFormData(prev => ({ ...prev, email }))}
                        onVerificationChange={setIsEmailVerified}
                        disabled={isSubmitting}
                        label="Email Address"
                        required
                      />
                    </div>

                    {/* Mobile and Subject - 2 column on sm+ */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Mobile Field */}
                      <div className="space-y-2">
                        <Label htmlFor="mobile" className="text-foreground text-sm">
                          <Phone className="w-4 h-4 inline mr-2" />
                          Mobile Number *
                        </Label>
                        <Input
                          id="mobile"
                          name="mobile"
                          type="tel"
                          value={formData.mobile}
                          onChange={handleInputChange}
                          className="glass border-border/30 focus:border-primary/50"
                          placeholder="Enter your mobile number"
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Subject Field */}
                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-foreground text-sm">
                          <FileText className="w-4 h-4 inline mr-2" />
                          Subject *
                        </Label>
                        <Input
                          id="subject"
                          name="subject"
                          type="text"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="glass border-border/30 focus:border-primary/50"
                          placeholder="Brief subject of your inquiry"
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Company Field */}
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-foreground text-sm">
                          Company
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="glass border-border/30 focus:border-primary/50"
                          placeholder="Your company name (optional)"
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Country Field */}
                      <div className="space-y-2">
                        <Label htmlFor="country" className="text-foreground text-sm">
                          <Globe className="w-4 h-4 inline mr-2" />
                          Country *
                        </Label>
                        <select
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 rounded-lg glass border border-input focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 [&>option]:bg-background [&>option]:text-foreground"
                          disabled={isSubmitting}
                        >
                          <option value="">Select your country</option>
                          {COUNTRIES.map((country) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </select>
                      </div>

                    </div>

                    {/* Message Field - Full width */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-foreground text-sm">
                        <MessageSquare className="w-4 h-4 inline mr-2" />
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="glass border-border/30 focus:border-primary/50 min-h-[80px] resize-none"
                        placeholder="Tell us more about your project or requirements"
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="pt-2">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        aria-disabled={isSubmitting}
                        className={`w-full bg-primary hover:bg-primary/90 text-primary-foreground neon-glow h-11 ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {isSubmitting ? (
                          <motion.div
                            className="flex items-center gap-2"
                            initial={{ opacity: 0.8 }}
                            animate={{ opacity: 1 }}
                            transition={{ repeat: Infinity, duration: 0.8, repeatType: 'reverse' }}
                          >
                            <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                            Sending...
                          </motion.div>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="w-4 h-4" />
                            Send Message
                          </span>
                        )}
                      </Button>
                    </div>

                    <p className="text-xs text-muted-foreground text-center">
                      * Required fields
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Render the modal in a portal to ensure it's at the top level of the DOM
  return typeof window !== 'undefined'
    ? createPortal(modalContent, document.body)
    : null;
};